import React, { useEffect, useState, useCallback } from "react";
import {
  Plus, Loader2, Trash2, Pencil, Truck, Phone, Mail, MessageCircle, Search, X,
  Package, MapPin, Tag, LayoutGrid, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api, brl, fmtDate, errMsg } from "@/lib/api";

const FRETES = ["CIF", "FOB", "Ambos", "A negociar"];
const DISPONIBILIDADES = ["Em estoque", "Sob encomenda", "Indisponível"];
const emptyProduto = () => ({ nome: "", descricao: "", categoria: "", subcategoria: "", marca: "", modelo: "", sku: "", unidade: "", preco: "", preco_atualizado_em: "", prazo: "", disponibilidade: "Em estoque", qtd_minima: "", observacoes: "" });

export default function Fornecedores() {
  const [list, setList] = useState(null);
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fUf, setFUf] = useState("");
  const [view, setView] = useState("categorias"); // categorias | lista

  const loadCats = useCallback(() => api.get("/suppliers/categorias").then((r) => setCats(r.data)).catch(() => {}), []);
  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (fCat) params.categoria = fCat;
    if (fStatus) params.status = fStatus;
    if (fUf) params.uf = fUf;
    return api.get("/suppliers", { params }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, fCat, fStatus, fUf]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...modal };
      if (modal.id) await api.put(`/suppliers/${modal.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success("Fornecedor salvo"); setModal(null); load(); loadCats();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.supplier) {
        toast.error(d.msg);
        if (window.confirm(`${d.msg}\n\nDeseja abrir o cadastro existente?`)) {
          const { data } = await api.get("/suppliers", { params: {} });
          const found = data.find((x) => x.id === d.supplier.id);
          if (found) setModal(found);
        }
      } else { toast.error(errMsg(e)); }
    }
  };
  const del = async (id) => { if (!window.confirm("Excluir este fornecedor?")) return; try { await api.delete(`/suppliers/${id}`); load(); loadCats(); } catch (e) { toast.error(errMsg(e)); } };

  const ufs = Array.from(new Set((list || []).map((s) => s.uf).filter(Boolean))).sort();

  if (!list) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>;

  const openCategory = (label) => { setFCat(label); setView("lista"); };

  return (
    <div className="animate-fade-up pb-10">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Fornecedores</h1>
          <p className="text-sm text-slate-500">{list.length} fornecedores · categorias derivadas dos seus CNAEs (Configurações)</p>
        </div>
        <button data-testid="add-supplier-btn" onClick={() => setModal({ categorias: [], produtos_list: [], frete: "A negociar", status: "ativo" })} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">
          <Plus className="h-4 w-4" /> Novo fornecedor
        </button>
      </div>

      {/* Barra de busca + filtros */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por razão social, CNPJ, produto, marca, SKU..." className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-sky-400" />
        </div>
        <select data-testid="supplier-filter-categoria" value={fCat} onChange={(e) => { setFCat(e.target.value); setView("lista"); }} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm">
          <option value="">Todas categorias</option>
          {cats.map((c) => <option key={c.label} value={c.label}>{c.label}</option>)}
        </select>
        <select data-testid="supplier-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm">
          <option value="">Status</option><option value="ativo">Ativo</option><option value="inativo">Inativo</option>
        </select>
        <select data-testid="supplier-filter-uf" value={fUf} onChange={(e) => setFUf(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm">
          <option value="">UF</option>
          {ufs.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <div className="flex overflow-hidden rounded-lg border border-slate-200">
          <button onClick={() => { setView("categorias"); setFCat(""); }} className={`px-3 py-2 text-xs font-semibold ${view === "categorias" ? "bg-sky-600 text-white" : "bg-white text-slate-600"}`}><LayoutGrid className="h-4 w-4" /></button>
          <button onClick={() => setView("lista")} className={`px-3 py-2 text-xs font-semibold ${view === "lista" ? "bg-sky-600 text-white" : "bg-white text-slate-600"}`}>Lista</button>
        </div>
      </div>

      {view === "categorias" && !q && !fStatus && !fUf ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {cats.map((c) => (
            <button key={c.label} data-testid="supplier-category-card" onClick={() => openCategory(c.label)} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:border-sky-400 hover:shadow-md">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Tag className="h-4 w-4 text-sky-500" /><span className="truncate">{c.label}</span></div>
                <div className="mt-1 text-xs text-slate-400">{c.cnae ? `CNAE ${c.cnae}` : "Sem CNAE"}</div>
              </div>
              <span className="ml-2 shrink-0 rounded-full bg-sky-50 px-2.5 py-1 text-sm font-bold text-sky-700">{c.count}</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          {fCat && (
            <button onClick={() => { setFCat(""); setView("categorias"); }} className="mb-3 flex items-center gap-1 text-sm font-semibold text-sky-600"><ChevronLeft className="h-4 w-4" /> Voltar às categorias · <span className="text-slate-700">{fCat}</span></button>
          )}
          {list.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Nenhum fornecedor encontrado.</div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {list.map((s) => (
                <div key={s.id} data-testid="supplier-card" className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-2 flex items-start justify-between">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-sky-50"><Truck className="h-4 w-4 text-sky-600" /></div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-800">{s.nome_fantasia || s.razao_social}</div>
                        <div className="truncate text-xs text-slate-400">{s.razao_social}</div>
                        <div className="text-xs text-slate-400">{s.cnpj}</div>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button data-testid="supplier-edit-btn" onClick={() => setModal({ ...s, produtos_list: s.produtos_list || [], categorias: s.categorias || [] })} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Pencil className="h-4 w-4" /></button>
                      <button data-testid="supplier-delete-btn" onClick={() => del(s.id)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {(s.categorias || []).length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {s.categorias.map((c) => <span key={c} className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">{c}</span>)}
                    </div>
                  )}
                  <div className="mb-2 flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" />{(s.produtos_list || []).length} produto(s)</span>
                    {(s.cidade || s.uf) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[s.cidade, s.uf].filter(Boolean).join("/")}</span>}
                  </div>
                  <div className="space-y-1 text-xs text-slate-600">
                    {s.telefone && <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" />{s.telefone}</div>}
                    {s.whatsapp && <div className="flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5 text-emerald-500" />{s.whatsapp}</div>}
                    {s.email && <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" />{s.email}</div>}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-3 text-[11px]">
                    {s.frete && <span className="rounded bg-slate-100 px-2 py-0.5">Frete: {s.frete}</span>}
                    {(s.prazo_dias || s.prazo) && <span className="rounded bg-slate-100 px-2 py-0.5">Prazo: {s.prazo_dias ? `${s.prazo_dias} dias úteis` : s.prazo}</span>}
                    {s.condicoes && <span className="rounded bg-slate-100 px-2 py-0.5">{s.condicoes}</span>}
                    <span className={`ml-auto rounded-full px-2 py-0.5 font-semibold ${(s.status || "ativo") === "ativo" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{(s.status || "ativo") === "ativo" ? "Ativo" : "Inativo"}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {modal && <SupplierModal modal={modal} setModal={setModal} cats={cats} onSave={save} />}
    </div>
  );
}

function SupplierModal({ modal, setModal, cats, onSave }) {
  const [tab, setTab] = useState("dados");
  const set = (patch) => setModal({ ...modal, ...patch });
  const toggleCat = (label) => set({ categorias: (modal.categorias || []).includes(label) ? modal.categorias.filter((c) => c !== label) : [...(modal.categorias || []), label] });
  const prods = modal.produtos_list || [];
  const setProds = (arr) => set({ produtos_list: arr });
  const addProd = () => setProds([...prods, emptyProduto()]);
  const setProd = (i, patch) => setProds(prods.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const rmProd = (i) => setProds(prods.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(null)} />
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-2xl" data-testid="supplier-modal">
        <div className="flex items-center justify-between border-b border-slate-200 p-5">
          <h3 className="font-display text-lg font-bold">{modal.id ? "Editar fornecedor" : "Novo fornecedor"}</h3>
          <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex gap-1 border-b border-slate-200 px-4">
          {[["dados", "Dados"], ["categorias", "Categorias"], ["produtos", `Produtos (${prods.length})`]].map(([k, l]) => (
            <button key={k} data-testid={`supplier-tab-${k}`} onClick={() => setTab(k)} className={`border-b-2 px-3 py-2.5 text-sm font-medium ${tab === k ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === "dados" && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[["razao_social", "Razão social"], ["nome_fantasia", "Nome fantasia"], ["cnpj", "CNPJ"], ["contato", "Contato"], ["telefone", "Telefone"], ["whatsapp", "WhatsApp"], ["email", "E-mail"], ["cidade", "Cidade"], ["uf", "UF"], ["condicoes", "Condições de pagamento"]].map(([k, label]) => (
                <div key={k}>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
                  <input data-testid={`supplier-input-${k}`} value={modal[k] ?? ""} onChange={(e) => set({ [k]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" />
                </div>
              ))}
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Frete</label>
                <select data-testid="supplier-input-frete" value={modal.frete ?? "A negociar"} onChange={(e) => set({ frete: e.target.value })} className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">
                  {FRETES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Prazo (dias úteis)</label>
                <input data-testid="supplier-input-prazo_dias" type="number" value={modal.prazo_dias ?? ""} onChange={(e) => set({ prazo_dias: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Status</label>
                <select data-testid="supplier-input-status" value={modal.status ?? "ativo"} onChange={(e) => set({ status: e.target.value })} className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">
                  <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Observações</label>
                <textarea value={modal.observacoes ?? ""} onChange={(e) => set({ observacoes: e.target.value })} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              {modal.produtos && !prods.length && (
                <div className="sm:col-span-2 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-700">Produtos (texto legado preservado): "{modal.produtos}" — cadastre-os na aba Produtos quando quiser.</div>
              )}
            </div>
          )}

          {tab === "categorias" && (
            <div>
              <p className="mb-2 text-xs text-slate-500">Categorias disponíveis conforme os CNAEs cadastrados em Configurações. Um fornecedor pode ter mais de uma.</p>
              <div className="flex flex-wrap gap-2">
                {cats.map((c) => (
                  <button key={c.label} data-testid="supplier-cat-toggle" onClick={() => toggleCat(c.label)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${(modal.categorias || []).includes(c.label) ? "border-sky-400 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{c.label}</button>
                ))}
              </div>
            </div>
          )}

          {tab === "produtos" && (
            <div className="space-y-3">
              <button data-testid="supplier-add-produto" onClick={addProd} className="flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-200"><Plus className="h-4 w-4" /> Adicionar produto</button>
              {prods.length === 0 && <p className="text-xs text-slate-400">Nenhum produto vinculado. Clique em "Adicionar produto".</p>}
              {prods.map((p, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid="supplier-produto-row">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-500">Produto #{i + 1}</span>
                    <button onClick={() => rmProd(i)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {[["nome", "Nome"], ["marca", "Marca"], ["modelo", "Modelo"], ["sku", "SKU/Código"], ["categoria", "Categoria"], ["subcategoria", "Subcategoria"], ["unidade", "Unidade"], ["preco", "Preço"], ["prazo", "Prazo (dias)"], ["qtd_minima", "Qtd mínima"], ["preco_atualizado_em", "Atualização preço"]].map(([k, l]) => (
                      <div key={k}>
                        <label className="block text-[10px] font-semibold uppercase text-slate-400">{l}</label>
                        <input type={k === "preco_atualizado_em" ? "date" : (["preco", "prazo", "qtd_minima"].includes(k) ? "number" : "text")} value={p[k] ?? ""} onChange={(e) => setProd(i, { [k]: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1 text-xs" />
                      </div>
                    ))}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase text-slate-400">Disponibilidade</label>
                      <select value={p.disponibilidade ?? "Em estoque"} onChange={(e) => setProd(i, { disponibilidade: e.target.value })} className="w-full rounded border border-slate-200 px-1 py-1 text-xs">
                        {DISPONIBILIDADES.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2 sm:col-span-3">
                      <label className="block text-[10px] font-semibold uppercase text-slate-400">Descrição / Observações</label>
                      <input value={p.descricao ?? ""} onChange={(e) => setProd(i, { descricao: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1 text-xs" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <button onClick={() => setModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
          <button data-testid="supplier-save-btn" onClick={onSave} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button>
        </div>
      </div>
    </div>
  );
}
